import { REQUIRE_NURSE_DELEGATION } from "../../../constants/constants";
import {
  buildCarePlanGeniePreview,
  CarePlanGenieFieldDefinition,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";

export interface MedicationManagementGenieSectionValues {
  anyAllergies?: boolean;
  allergiesDescribed?: string;
  needMoreThanOneMedication?: boolean;
  anyPsychopharmacologicMedication?: boolean;
  seeMARForMedicationDetails?: boolean;
  medsOrderedBy?: string;
  medsDeliveredBy?: string;
  medsPharmacyPacked?: boolean;
  medsPharmacyPackedDetails?: string;
  selfAdministerMedications?: boolean;
  selfAdministerMedicationsWithAssistance?: boolean;
  medicationAdministeredByStaff?: boolean;
  medicationManagementTypes?: string[];
  nurseDelegationContact?: string;
  medicationStrengthsDetails?: string;
  medicationAssistanceDetails?: string;
  medicationAmountReasonableDetails?: string;
  medicationPlanOutsideAFHStrengthDetails?: string;
  medicationPlanOutsideAFHAssistanceDetails?: string;
  medicalRefusalPlanStrengthDetails?: string;
  medicalRefusalPlanAssistanceDetails?: string;
}

export const MEDICATION_MANAGEMENT_GENIE_FIELD_DEFINITIONS: Record<string, CarePlanGenieFieldDefinition> = {
  anyAllergies: { key: "anyAllergies", label: "Any medication allergies", type: "boolean", applyMode: "replace" },
  allergiesDescribed: { key: "allergiesDescribed", label: "Allergies described", type: "text", applyMode: "append" },
  needMoreThanOneMedication: { key: "needMoreThanOneMedication", label: "Resident needs more than one medication", type: "boolean", applyMode: "replace" },
  anyPsychopharmacologicMedication: { key: "anyPsychopharmacologicMedication", label: "Any psychopharmacologic medication prescribed", type: "boolean", applyMode: "replace" },
  seeMARForMedicationDetails: { key: "seeMARForMedicationDetails", label: "See MAR for medication details", type: "boolean", applyMode: "replace" },
  medsOrderedBy: { key: "medsOrderedBy", label: "Medications ordered by", type: "text", applyMode: "append" },
  medsDeliveredBy: { key: "medsDeliveredBy", label: "Medications delivered by", type: "text", applyMode: "append" },
  medsPharmacyPacked: { key: "medsPharmacyPacked", label: "Are medications pharmacy packed", type: "boolean", applyMode: "replace" },
  medsPharmacyPackedDetails: { key: "medsPharmacyPackedDetails", label: "Medications pharmacy packed details", type: "text", applyMode: "append" },
  selfAdministerMedications: { key: "selfAdministerMedications", label: "Any self-administer medications", type: "boolean", applyMode: "replace" },
  selfAdministerMedicationsWithAssistance: { key: "selfAdministerMedicationsWithAssistance", label: "Any self-administer medications with assistance", type: "boolean", applyMode: "replace" },
  medicationAdministeredByStaff: { key: "medicationAdministeredByStaff", label: "Any medication administered by caregiver", type: "boolean", applyMode: "replace" },
  medicationManagementTypes: { key: "medicationManagementTypes", label: "Medication management types", type: "stringArray", applyMode: "mergeArray" },
  nurseDelegationContact: { key: "nurseDelegationContact", label: "Nurse delegation contact details", type: "text", applyMode: "append" },
  medicationStrengthsDetails: { key: "medicationStrengthsDetails", label: "Medication strengths details", type: "text", applyMode: "append" },
  medicationAssistanceDetails: { key: "medicationAssistanceDetails", label: "Medication assistance details", type: "text", applyMode: "append" },
  medicationAmountReasonableDetails: { key: "medicationAmountReasonableDetails", label: "Reason for medication assistance amount", type: "text", applyMode: "append" },
  medicationPlanOutsideAFHStrengthDetails: { key: "medicationPlanOutsideAFHStrengthDetails", label: "Medication outside facility details", type: "text", applyMode: "append" },
  medicationPlanOutsideAFHAssistanceDetails: { key: "medicationPlanOutsideAFHAssistanceDetails", label: "Medication outside facility assistance details", type: "text", applyMode: "append" },
  medicalRefusalPlanStrengthDetails: { key: "medicalRefusalPlanStrengthDetails", label: "Medication refusal details", type: "text", applyMode: "append" },
  medicalRefusalPlanAssistanceDetails: { key: "medicalRefusalPlanAssistanceDetails", label: "Medication refusal assistance details", type: "text", applyMode: "append" },
};

export const MEDICATION_MANAGEMENT_GENIE_QUESTIONS: CarePlanGenieQuestion[] = [
  {
    id: "allergies",
    title: "Allergies",
    prompt: "What medication allergy or medication-profile details should be documented for this resident?",
    editableFieldKeys: ["allergiesDescribed"],
    options: [
      {
        id: "med_allergy_none",
        label: "No known medication allergies",
        description: "No medication allergy concerns reported right now.",
        updates: { anyAllergies: false },
      },
      {
        id: "med_allergy_known",
        label: "Known medication allergy",
        description: "Important medication trigger or reaction history should be included.",
        updates: {
          anyAllergies: true,
          allergiesDescribed: "Resident has a known medication allergy that should remain clearly listed in the care plan and medication records.",
        },
      },
      {
        id: "med_multiple",
        label: "Uses multiple medications",
        description: "Medication profile is more involved and needs closer organization.",
        updates: {
          needMoreThanOneMedication: true,
          medicationAmountReasonableDetails: "Resident takes multiple medications and needs an organized medication routine to support safe administration and follow-up.",
        },
      },
      {
        id: "med_psychotropic",
        label: "Psychopharmacologic medication is prescribed",
        description: "Behavioral or psychiatric medication monitoring is relevant.",
        updates: {
          anyPsychopharmacologicMedication: true,
          medicationStrengthsDetails: "Resident has psychopharmacologic medication prescribed and benefits from monitoring for effectiveness, side effects, and behavior changes.",
        },
      },
    ],
  },
  {
    id: "packaging",
    title: "Packaging and Delivery",
    prompt: "What should be documented about how medications are ordered, delivered, and packaged?",
    editableFieldKeys: ["medsOrderedBy", "medsDeliveredBy", "medsPharmacyPackedDetails"],
    options: [
      {
        id: "pack_mar",
        label: "Use MAR as the main medication reference",
        description: "Medication details are primarily tracked on the MAR.",
        updates: { seeMARForMedicationDetails: true },
      },
      {
        id: "pack_provider_orders",
        label: "Ordered by prescribing provider",
        description: "Medication orders come from the resident's prescriber or clinic team.",
        updates: {
          medsOrderedBy: "Medications are ordered by the prescribing provider and updated based on current medical orders.",
        },
      },
      {
        id: "pack_pharmacy_delivery",
        label: "Delivered by pharmacy",
        description: "Pharmacy provides or coordinates medication delivery.",
        updates: {
          medsDeliveredBy: "Medications are delivered through the pharmacy and coordinated with the home as needed.",
        },
      },
      {
        id: "pack_pharmacy_packed",
        label: "Pharmacy packed",
        description: "Bubble packs, med sets, or other pharmacy packing is used.",
        updates: {
          medsPharmacyPacked: true,
          medsPharmacyPackedDetails: "Medications are pharmacy packed to support safe storage, organization, and administration.",
        },
      },
    ],
  },
  {
    id: "administration",
    title: "Administration",
    prompt: "How does the resident receive medications, and what management types apply?",
    editableFieldKeys: ["nurseDelegationContact", "medicationAssistanceDetails"],
    options: [
      {
        id: "admin_self",
        label: "Self-administers medications",
        description: "Resident can manage medications independently.",
        updates: {
          selfAdministerMedications: true,
          medicationAssistanceDetails: "Resident is generally able to self-administer medications with routine oversight for safety and compliance.",
        },
      },
      {
        id: "admin_self_assist",
        label: "Self-administers with assistance",
        description: "Resident needs setup, reminders, or cueing.",
        updates: {
          selfAdministerMedicationsWithAssistance: true,
          medicationAssistanceDetails: "Caregiver may need to provide setup, reminders, cueing, or observation to support the resident's self-administration routine.",
        },
      },
      {
        id: "admin_staff",
        label: "Caregiver administer medications",
        description: "Caregiver provide direct medication administration.",
        updates: {
          medicationAdministeredByStaff: true,
          medicationAssistanceDetails: "Caregiver provide direct medication administration and monitor for routine medication-related concerns.",
        },
      },
      {
        id: "admin_oral_topical",
        label: "Oral and topical medication support",
        description: "Standard oral or topical medication handling is part of care.",
        updates: {
          medicationManagementTypes: ["Oral", "Topical"],
        },
      },
      {
        id: "admin_nurse_delegation",
        label: "Requires nurse delegation",
        description: "Medication support includes delegated tasks under RN oversight.",
        updates: {
          medicationManagementTypes: [REQUIRE_NURSE_DELEGATION],
          nurseDelegationContact: "Medication support includes tasks completed under nurse delegation. Keep the RN contact and delegation plan current.",
        },
      },
    ],
  },
  {
    id: "support",
    title: "Support Needs",
    prompt: "What should be documented about the resident's medication strengths, support needs, and why this level of help is needed?",
    editableFieldKeys: ["medicationStrengthsDetails", "medicationAssistanceDetails", "medicationAmountReasonableDetails"],
    options: [
      {
        id: "support_understands",
        label: "Understands medication routine",
        description: "Resident has some awareness of purpose or schedule.",
        updates: {
          medicationStrengthsDetails: "Resident is familiar with parts of the medication routine and may recognize medications, times, or general treatment purpose.",
        },
      },
      {
        id: "support_cueing",
        label: "Needs reminders or cueing",
        description: "Prompting helps keep medication administration safe and consistent.",
        updates: {
          medicationAssistanceDetails: "Resident benefits from reminders, cueing, and observation to make sure medications are taken correctly and at the right time.",
          medicationAmountReasonableDetails: "This level of assistance is reasonable because reminders and supervision help reduce missed doses, confusion, and medication errors.",
        },
      },
      {
        id: "support_complex",
        label: "Complex routine or safety monitoring",
        description: "Medication profile needs closer oversight.",
        updates: {
          medicationStrengthsDetails: "Resident's medication routine may be complex and requires careful tracking for dose timing, side effects, and treatment changes.",
          medicationAmountReasonableDetails: "The resident needs this amount of medication assistance due to the complexity of the regimen and the need for consistent safety oversight.",
        },
      },
    ],
  },
  {
    id: "outside",
    title: "Outside Facility",
    prompt: "What should be documented about medication support outside the home, including appointments, outings, or transitions?",
    editableFieldKeys: ["medicationPlanOutsideAFHStrengthDetails", "medicationPlanOutsideAFHAssistanceDetails"],
    options: [
      {
        id: "outside_none",
        label: "No special outside-facility medication concerns",
        description: "No extra outside support details need to be added.",
        updates: {},
      },
      {
        id: "outside_take_along",
        label: "Medications may need to travel with resident",
        description: "Outings or appointments may require medication coordination.",
        updates: {
          medicationPlanOutsideAFHStrengthDetails: "Medication planning should account for appointments, outings, or time away from the home when doses may still be due.",
          medicationPlanOutsideAFHAssistanceDetails: "Caregiver should help make sure needed medications, instructions, and timing information are coordinated when the resident is away from the home.",
        },
      },
      {
        id: "outside_family",
        label: "Family or outside providers may be involved",
        description: "Medication communication across settings should stay clear.",
        updates: {
          medicationPlanOutsideAFHAssistanceDetails: "Caregiver should communicate clearly with family or outside providers when medication support is shared across settings.",
        },
      },
    ],
  },
  {
    id: "refusal",
    title: "Refusal Plan",
    prompt: "What should be included about medication refusal risk and the plan if the resident declines medications?",
    editableFieldKeys: ["medicalRefusalPlanStrengthDetails", "medicalRefusalPlanAssistanceDetails"],
    options: [
      {
        id: "refusal_low",
        label: "No frequent refusal pattern",
        description: "Resident usually accepts medications as scheduled.",
        updates: {
          medicalRefusalPlanStrengthDetails: "Resident usually accepts medications as scheduled and does not show a frequent pattern of refusal.",
        },
      },
      {
        id: "refusal_occasional",
        label: "Occasional refusal or hesitation",
        description: "Resident may need calm explanation or re-approach.",
        updates: {
          medicalRefusalPlanStrengthDetails: "Resident may occasionally hesitate or refuse medications and benefits from calm explanation and reassurance.",
          medicalRefusalPlanAssistanceDetails: "Caregiver should re-approach respectfully, explain the purpose of the medication, document refusals, and notify the provider or nurse as needed.",
        },
      },
      {
        id: "refusal_monitoring",
        label: "Refusals should be monitored closely",
        description: "Repeated refusals could affect stability or treatment goals.",
        updates: {
          medicalRefusalPlanAssistanceDetails: "Caregiver should monitor repeated refusals, document patterns, and escalate concerns when refusals create a health or safety risk.",
        },
      },
    ],
  },
];

export const buildMedicationManagementGeniePreview = (
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
) => buildCarePlanGeniePreview(
  MEDICATION_MANAGEMENT_GENIE_FIELD_DEFINITIONS,
  MEDICATION_MANAGEMENT_GENIE_QUESTIONS,
  currentValues,
  selectedOptions,
  questionDrafts,
);
